import { z } from 'zod';

/**
 * The push re-engagement nudge (NM-272, docs/51).
 *
 * NM-271 raised the check cadence so a subject is refreshed several times a day;
 * NM-251 sends a Web Push tickle when a check lands fresh stories. Neither reaches
 * a person who never turned notifications on — and nothing invited them to. This
 * module decides **whether** to invite them, and **when to stop asking**. It is
 * pure: the banner JSX (`app.tsx`) renders whatever `pushNudgeVariant` says, and
 * the store owns the persisted snooze record.
 *
 * Owner decisions (NM-272 feedback):
 *  - **Placement**: a strip in `#banners`, like the failure/maintenance banners.
 *  - **Timing**: only once the feed has at least one story, so "get a heads-up
 *    when fresh stories land" is a promise about something the reader can see.
 *  - **iOS Safari** delivers Web Push only to a Home-Screen-installed site: there
 *    the nudge says so ("Add to Home Screen, then turn on notifications") and
 *    offers no button, since `PushManager` is not available to subscribe with.
 *  - **Dismissal is a schedule, not a switch**: "Not now" snoozes it for 15 days,
 *    then 30, then 90, then never — per device, in localStorage, like the
 *    sidebar preference.
 */

/** localStorage key for the per-device snooze record. */
export const PUSH_NUDGE_KEY = 'news:push-nudge';

const DAY_MS = 24 * 60 * 60 * 1000;

/**
 * How long each successive "Not now" hides the nudge, in days. A dismissal past
 * the end of this list hides it for good (owner: "15 days then 30 days then 90
 * days then never").
 */
export const PUSH_NUDGE_SNOOZE_DAYS: readonly number[] = [15, 30, 90];

/**
 * What a device remembers about the nudge. `until` is the epoch-ms the current
 * snooze ends, or `null` once the schedule is exhausted (never show again).
 */
export interface PushNudgeRecord {
  dismissals: number;
  until: number | null;
}

const PushNudgeRecordSchema = z.object({
  dismissals: z.number().int().min(0),
  until: z.number().nullable(),
});

/** A minimal storage seam so the store can pass `localStorage` and tests a Map. */
export interface NudgeStorage {
  getItem(key: string): string | null;
  setItem(key: string, value: string): void;
}

/**
 * Read the device's record; `null` when there is none, when storage is
 * unavailable, or when what is there does not parse — a corrupt record must
 * fail *open* (show the nudge) rather than throw during the first render.
 */
export function readPushNudgeRecord(storage: NudgeStorage | undefined = defaultStorage()): PushNudgeRecord | null {
  if (storage === undefined) return null;
  try {
    const raw = storage.getItem(PUSH_NUDGE_KEY);
    if (raw === null) return null;
    const parsed = PushNudgeRecordSchema.safeParse(JSON.parse(raw));
    return parsed.success ? parsed.data : null;
  } catch {
    return null;
  }
}

/** Persist a record; a storage failure (private mode, quota) is swallowed — the in-memory state still hides the nudge for this session. */
export function writePushNudgeRecord(
  record: PushNudgeRecord,
  storage: NudgeStorage | undefined = defaultStorage(),
): void {
  if (storage === undefined) return;
  try {
    storage.setItem(PUSH_NUDGE_KEY, JSON.stringify(record));
  } catch {
    /* storage disabled */
  }
}

function isNudgeStorage(value: unknown): value is NudgeStorage {
  return (
    typeof value === 'object' &&
    value !== null &&
    typeof Reflect.get(value, 'getItem') === 'function' &&
    typeof Reflect.get(value, 'setItem') === 'function'
  );
}

function defaultStorage(): NudgeStorage | undefined {
  try {
    // Looked up on `globalThis` rather than named: a non-browser import (the
    // unit suite) has no `localStorage`, and some browser contexts throw on the
    // accessor itself (storage disabled) — both must read as "no storage".
    const candidate: unknown = Reflect.get(globalThis, 'localStorage');
    return isNudgeStorage(candidate) ? candidate : undefined;
  } catch {
    return undefined;
  }
}

/**
 * The record after one more "Not now" at `now`: the next snooze on the schedule,
 * or `until: null` (never again) once the schedule is used up.
 */
export function dismissPushNudge(previous: PushNudgeRecord | null, now: number): PushNudgeRecord {
  const dismissals = (previous?.dismissals ?? 0) + 1;
  const days = PUSH_NUDGE_SNOOZE_DAYS.at(dismissals - 1);
  return { dismissals, until: days === undefined ? null : now + days * DAY_MS };
}

/** Whether a record is currently hiding the nudge. No record → not snoozed. */
export function pushNudgeSnoozed(record: PushNudgeRecord | null, now: number): boolean {
  if (record === null) return false;
  if (record.until === null) return true; // schedule exhausted: never again
  return now < record.until;
}

/** What the browser reports, or `'unsupported'` when there is no `Notification` API at all. */
export type NotificationPermissionState = NotificationPermission | 'unsupported';

/**
 * The pieces of `navigator`/`window` the iOS check reads, as a plain object so
 * the detection is unit-testable without a DOM.
 */
export interface PlatformProbe {
  userAgent: string;
  maxTouchPoints: number;
  /** Safari's non-standard flag: `true` when launched from the Home Screen. */
  standalone: boolean | undefined;
  /** `matchMedia('(display-mode: standalone)').matches` — the standard equivalent. */
  displayModeStandalone: boolean;
}

/**
 * iOS/iPadOS Safari running as a plain browser tab — where Web Push is simply not
 * offered until the site is added to the Home Screen. iPadOS reports a desktop
 * `Macintosh` UA, so the iPad case is "Mac UA with touch points". Any other
 * browser, and iOS already installed (standalone), returns false.
 */
export function isIosNeedingInstall(probe: PlatformProbe): boolean {
  const ua = probe.userAgent;
  const iphone = /iPhone|iPad|iPod/.test(ua);
  const ipadDesktopUa = /Macintosh/.test(ua) && probe.maxTouchPoints > 1;
  if (!iphone && !ipadDesktopUa) return false;
  const installed = probe.standalone === true || probe.displayModeStandalone;
  return !installed;
}

/** Read the live browser into a `PlatformProbe`; guarded so a non-browser import can't throw. */
export function livePlatformProbe(): PlatformProbe {
  if (typeof navigator === 'undefined')
    return { userAgent: '', maxTouchPoints: 0, standalone: undefined, displayModeStandalone: false };
  // Safari's non-standard `navigator.standalone`, read without widening the type.
  const standalone = Reflect.get(navigator, 'standalone') === true ? true : undefined;
  let displayModeStandalone = false;
  try {
    displayModeStandalone = typeof matchMedia === 'function' && matchMedia('(display-mode: standalone)').matches;
  } catch {
    /* no matchMedia */
  }
  return {
    userAgent: navigator.userAgent,
    maxTouchPoints: navigator.maxTouchPoints,
    standalone,
    displayModeStandalone,
  };
}

/** The live `Notification.permission`, or `'unsupported'`. */
export function livePermissionState(): NotificationPermissionState {
  return typeof Notification === 'undefined' ? 'unsupported' : Notification.permission;
}

/** Everything the decision reads — from the store, the device record, and the browser. */
export interface PushNudgeInput {
  /** `state.pushPublicKey` — null when the deploy has no Web Push configured. */
  pushPublicKey: string | null;
  /** The reader's notify setting; the nudge exists to turn it on. */
  notifyOnNewItems: boolean;
  /** The feed has answered at least once (NM-249) … */
  feedLoaded: boolean;
  /** … and holds at least one story (owner: only then is the promise concrete). */
  storyCount: number;
  /** The device's snooze record, or null. */
  record: PushNudgeRecord | null;
  now: number;
  /** The reader tried to enable from this nudge (or Settings) and the browser refused, this session. */
  permissionDenied: boolean;
  permission: NotificationPermissionState;
  iosNeedingInstall: boolean;
}

/**
 * Which nudge to show, if any:
 *  - `'hidden'` — nothing to invite (no push on this deploy, already on, no
 *    stories yet, snoozed, or the browser cannot deliver and has said so).
 *  - `'ready'` — the invitation with a "Turn on notifications" button.
 *  - `'ios-install'` — iOS Safari in a tab: explain Add to Home Screen, no button.
 *  - `'blocked'` — the reader just tried and the browser refused: say where to
 *    fix it. Only after an attempt; a browser that was already `denied` before
 *    any click gets nothing, since a nudge that leads nowhere is noise.
 */
export type PushNudgeVariant = 'hidden' | 'ready' | 'ios-install' | 'blocked';

export function pushNudgeVariant(input: PushNudgeInput): PushNudgeVariant {
  if (input.pushPublicKey === null) return 'hidden';
  if (input.notifyOnNewItems) return 'hidden';
  if (!input.feedLoaded || input.storyCount <= 0) return 'hidden';
  if (pushNudgeSnoozed(input.record, input.now)) return 'hidden';
  if (input.iosNeedingInstall) return 'ios-install';
  if (input.permissionDenied) return 'blocked';
  if (input.permission === 'unsupported' || input.permission === 'denied') return 'hidden';
  return 'ready';
}
